import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Link,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Box,
} from '@mui/material';
import dayjs from 'dayjs';
import { Launch, Add } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';

import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getReciprocitySPDChapter,
  createReciprocity,
  checkFinancialPermission,
} from '../../../endpoints/reciprocity';
import { reciprocityTypes, roles } from '../../../utils/variables';
import {
  ReciprocitySPDChapter,
  ReciprocityCreate,
} from '../../../types/reciprocity';
import AlertItem from '../../../Components/AlertItem';

interface SPDGroupsReciprocitiesProps {
  id: string;
}

const SPDGroupsReciprocities: React.FC<SPDGroupsReciprocitiesProps> = ({
  id,
}) => {
  const [search, setSearch] = useState<string>('');
  const [filteredReciprocities, setFilteredReciprocities] = useState<
    ReciprocitySPDChapter[]
  >([]);
  const [reciprocityType, setReciprocityType] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
  const [createData, setCreateData] = useState<Partial<ReciprocityCreate>>({
    name: '',
    reciprocity_type_id: '',
    is_active: true,
  });
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [canEditFinancial, setCanEditFinancial] = useState<boolean>(false);

  // Check financial permission
  const { data: financialPermission } = useApiGet<{
    can_edit_financial: boolean;
  }>(['reciprocity-financial-permission'], checkFinancialPermission, {
    enabled: true,
    retry: 1,
  });

  useEffect(() => {
    if (financialPermission) {
      setCanEditFinancial(financialPermission.can_edit_financial);
    }
  }, [financialPermission]);

  const {
    data: testData,
    isLoading,
    refetch,
  } = useApiGet<ReciprocitySPDChapter[]>(
    ['spd-reciprocities', id],
    getReciprocitySPDChapter,
    {
      enabled: !!id,
      retry: 1,
    },
    id
  );

  const { mutate: createReciprocityMutation, isPending: isCreating } =
    useApiSend(
      (data: ReciprocityCreate) => createReciprocity(data),
      () => {
        setShowCreateDialog(false);
        setCreateData({
          name: '',
          reciprocity_type_id: '',
          is_active: true,
          financial_name: undefined,
          tax_id_number: undefined,
        });
        setSuccessMessage('Reciprocity created successfully!');
        refetch();
        setTimeout(() => setSuccessMessage(''), 3000);
      },
      (error: Error) => {
        setErrorMessage('Failed to create reciprocity. Please try again.');
        console.error('Error creating reciprocity:', error);
      }
    );

  useEffect(() => {
    if (testData && testData.length > 0) {
      let filtered = testData.filter((reciprocity) => {
        // Search filter
        const matchesSearch =
          search.length === 0 ||
          reciprocity.name.toLowerCase().includes(search.toLowerCase());

        // Type filter
        const matchesType =
          reciprocityType === 'all' ||
          reciprocity.reciprocity_type_id === reciprocityType;

        // Status filter
        const matchesStatus =
          statusFilter === 'all' ||
          (statusFilter === 'active' && reciprocity.is_active) ||
          (statusFilter === 'inactive' && !reciprocity.is_active);

        return matchesSearch && matchesType && matchesStatus;
      });

      setFilteredReciprocities(filtered);
    } else {
      setFilteredReciprocities([]);
    }
  }, [testData, search, reciprocityType, statusFilter]);

  const canCreateReciprocity = () => {
    const userRole = localStorage.getItem('role');
    return userRole === roles.Admin || userRole === roles.Support;
  };

  const handleCreateReciprocity = () => {
    if (createData.name && createData.reciprocity_type_id) {
      const createPayload: ReciprocityCreate = {
        name: createData.name,
        reciprocity_type_id: createData.reciprocity_type_id,
        spd_chapter_id: id,
        is_active: createData.is_active || true,
      };

      // Only include financial fields if user has permission
      if (canEditFinancial) {
        if (createData.financial_name) {
          createPayload.financial_name = createData.financial_name;
        }
        if (createData.tax_id_number) {
          createPayload.tax_id_number = createData.tax_id_number;
        }
      }

      createReciprocityMutation(createPayload);
    }
  };

  if (isLoading) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }

  if (!filteredReciprocities) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }

  return (
    <Grid container>
      {/* Create Button */}
      {canCreateReciprocity() && (
        <Grid item xs={12} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={() => setShowCreateDialog(true)}
              disabled={isCreating}
            >
              Create Reciprocity
            </Button>
          </Box>
        </Grid>
      )}

      {/* Messages */}
      {errorMessage && (
        <Grid item xs={12} sx={{ mb: 2 }}>
          <AlertItem
            severity="error"
            description={errorMessage}
            onClose={() => setErrorMessage('')}
          />
        </Grid>
      )}
      {successMessage && (
        <Grid item xs={12} sx={{ mb: 2 }}>
          <AlertItem
            severity="success"
            description={successMessage}
            onClose={() => setSuccessMessage('')}
          />
        </Grid>
      )}

      {(!filteredReciprocities || filteredReciprocities?.length === 0) && (
        <Grid item xs={12} textAlign={'center'} paddingTop={2}>
          <Typography variant="body1">
            No groups or reciprocities assigned
          </Typography>
        </Grid>
      )}

      {testData && testData.length > 2 && (
        <>
          <Grid item xs={6} sx={{ p: 1 }}>
            <TextField
              label="Search"
              id="search-field"
              size="small"
              value={search}
              fullWidth={true}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={3} sx={{ p: 1 }}>
            <FormControl size={'small'} fullWidth={true}>
              <InputLabel id="reciprocity-type-label">Type</InputLabel>
              <Select
                labelId="reciprocity-type-label"
                id="reciprocity-type-select"
                value={reciprocityType}
                onChange={(event) => setReciprocityType(event.target.value)}
                sx={{ textAlign: 'left' }}
                label="Type"
              >
                <MenuItem value={'all'}>All</MenuItem>
                <MenuItem value={reciprocityTypes.Reciprocity}>
                  Reciprocity
                </MenuItem>
                <MenuItem value={reciprocityTypes.Group}>Group</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3} sx={{ p: 1 }}>
            <FormControl size={'small'} fullWidth={true}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter-select"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                sx={{ textAlign: 'left' }}
                label="Status"
              >
                <MenuItem value={'all'}>All</MenuItem>
                <MenuItem value={'active'}>Active</MenuItem>
                <MenuItem value={'inactive'}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </>
      )}

      {filteredReciprocities?.map((reciprocity) => (
        <Grid item xs={12} md={6} sx={{ p: 1 }} key={reciprocity.id}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={reciprocity.name}
              subheader={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">
                    {reciprocity.reciprocity_type_id ===
                    reciprocityTypes.Reciprocity
                      ? 'Reciprocity'
                      : 'Group'}
                  </Typography>
                  <Chip
                    label={reciprocity.is_active ? 'Active' : 'Inactive'}
                    color={reciprocity.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </Stack>
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              <Grid container spacing={2}>
                {reciprocity.members
                  .sort((a, b) => a.order - b.order)
                  .map((member) => (
                    <Grid container item xs={12} key={member.id}>
                      <Grid item xs={12}>
                        <Typography variant="body1" fontWeight={'bold'}>
                          {member.role}
                        </Typography>
                        <Typography variant="body1">{member.name}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          fontSize={12}
                          paddingBottom={1}
                        >
                          {dayjs(member.start_date).format('MM/DD/YYYY')} -
                        </Typography>
                        <Typography
                          variant="caption"
                          fontSize={12}
                          paddingBottom={1}
                          paddingLeft={1}
                        >
                          {member.end_date
                            ? dayjs(member.end_date).format('MM/DD/YYYY')
                            : 'Present'}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
              </Grid>
            </CardContent>
            <CardActions>
              <Grid item xs={12} textAlign={'right'}>
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`/reciprocity/${reciprocity.id}`}
                  underline="hover"
                  color="primary"
                >
                  View Details
                  <Launch sx={{ marginLeft: 1, fontSize: 16 }} />
                </Link>
              </Grid>
            </CardActions>
          </Card>
        </Grid>
      ))}

      {/* Create Reciprocity Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Reciprocity</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Name"
              value={createData.name || ''}
              onChange={(e) =>
                setCreateData({ ...createData, name: e.target.value })
              }
              required
            />
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={createData.reciprocity_type_id || ''}
                onChange={(e) =>
                  setCreateData({
                    ...createData,
                    reciprocity_type_id: e.target.value,
                  })
                }
                label="Type"
                required
              >
                <MenuItem value={reciprocityTypes.Reciprocity}>
                  Reciprocity
                </MenuItem>
                <MenuItem value={reciprocityTypes.Group}>Group</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={createData.is_active ? 'true' : 'false'}
                onChange={(e) =>
                  setCreateData({
                    ...createData,
                    is_active: e.target.value === 'true',
                  })
                }
                label="Status"
              >
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
            {canEditFinancial && (
              <>
                <TextField
                  fullWidth
                  label="Financial Name"
                  value={createData.financial_name || ''}
                  onChange={(e) =>
                    setCreateData({
                      ...createData,
                      financial_name: e.target.value,
                    })
                  }
                />
                <TextField
                  fullWidth
                  label="Tax ID Number"
                  value={createData.tax_id_number || ''}
                  onChange={(e) =>
                    setCreateData({
                      ...createData,
                      tax_id_number: e.target.value,
                    })
                  }
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              setShowCreateDialog(false);
              setCreateData({
                name: '',
                reciprocity_type_id: '',
                is_active: true,
                financial_name: undefined,
                tax_id_number: undefined,
              });
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleCreateReciprocity}
            disabled={
              !createData.name || !createData.reciprocity_type_id || isCreating
            }
          >
            Create Reciprocity
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default SPDGroupsReciprocities;
